import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { concat, forkJoin, Observable, of, zip } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { DataService } from 'src/app/shared/data.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class WaterQualityService {
  private endPoint = `${environment.api}/water-qualities`;

  private dyName = 'water_quality';

  private updateSampleChartsUrl = `${environment.api}/sample-charts`;

  private updateMeasurementPointsUrl = `${environment.api}/measurement-points`;

  constructor(private http: HttpClient, private dataService: DataService) {}

  fetchData(): Observable<any> {
    return this.dataService.selectedYear.pipe(
      switchMap((dy) => {
        if (dy[this.dyName]) {
          return this.http.get(`${this.endPoint}/${dy[this.dyName]}`).pipe(
            switchMap((waterQualityData: any) => {
              console.log('Fetched water_quality data:', waterQualityData);
              const sampleFields = ['groundwater_quality', 'effluent_quality', 'surface_quality'];
              const sampleRequests: Observable<any>[] = [];

              // For each sample field, if it has an ID, fetch the full sample-chart
              sampleFields.forEach(fieldName => {
                const sampleId = waterQualityData[fieldName];
                if (sampleId && typeof sampleId === 'number') {
                  console.log(`Fetching sample-chart ${sampleId} for field ${fieldName}`);
                  sampleRequests.push(
                    this.http.get(`${this.updateSampleChartsUrl}/${sampleId}`).pipe(
                      map(sampleData => ({ fieldName, sampleData }))
                    )
                  );
                }
              });

              // If no samples to fetch, return water_quality data as-is
              if (sampleRequests.length === 0) {
                console.log('No sample-charts to fetch');
                return of(waterQualityData);
              }

              // Fetch all sample-charts in parallel
              return forkJoin(sampleRequests).pipe(
                map(samples => {
                  console.log('Fetched samples:', samples);
                  // Replace IDs with full sample objects
                  samples.forEach(({ fieldName, sampleData }) => {
                    waterQualityData[fieldName] = sampleData;
                  });
                  console.log('Final water_quality data with populated samples:', waterQualityData);
                  return waterQualityData;
                })
              );
            })
          );
        } else {
          return this.http.post(`${this.endPoint}`, {
            data_year: dy.id,
          });
        }
      })
    );
  }

  updateData(formValues: any): Observable<any[]> {
    for (const key of Object.keys(formValues)) {
      if (formValues[key].data?.id === undefined) delete formValues[key];
    }
    const samplesAndPoints = this.splitFormValues(formValues);

    // Get water_quality ID from current year using Observable pattern
    return this.dataService.selectedYear.pipe(
      switchMap((dy) => {
        const updateRequests: Observable<any>[] = [];
        const waterQualityId = dy[this.dyName];

        if (formValues.formData) {
          const formData = formValues.formData.data;
          updateRequests.push(
            this.http.put(`${this.endPoint}/${formData.id}`, formData)
          );
        }

        samplesAndPoints.sampleCharts.forEach((sample) => {
          if (sample.id) {
            updateRequests.push(
              this.http.put(`${this.updateSampleChartsUrl}/${sample.id}`, {
                ...sample,
                water_quality: waterQualityId
              })
            );
          }
        });
        samplesAndPoints.measurementPoints.forEach((mp) => {
          if (mp.id) {
            console.log(`Updating measurement-point ${mp.id}:`, JSON.stringify(mp, null, 2));
            updateRequests.push(
              this.http.put(`${this.updateMeasurementPointsUrl}/${mp.id}`, mp).pipe(
                tap({
                  error: (error) => {
                    console.error(`Failed to update measurement-point ${mp.id}:`, error);
                    console.error('Measurement point data that failed:', JSON.stringify(mp, null, 2));
                    if (error.error?.data) {
                      console.error('Validation error details:', error.error.data);
                    }
                  }
                })
              )
            );
          }
        });

        return updateRequests.length > 0 ? forkJoin(updateRequests) : of([]);
      })
    );
  }

  private splitFormValues(formValues: any): {
    sampleCharts: any[];
    measurementPoints: any[];
  } {
    const sampleCharts: any[] = [];
    const measurementPoints: any[] = [];
    for (const key of Object.keys(formValues)) {
      if (formValues[key].data && Object.keys(formValues[key].data).length > 0) {
        if (formValues[key].data.points) {
          const points = formValues[key].data.points as Array<any>;
          delete formValues[key].data.points;
          points.forEach((p) => measurementPoints.push(p));
        }
        sampleCharts.push(formValues[key].data);
      }
    }
    return { sampleCharts, measurementPoints };
  }
}
